import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileCheck, Upload, FileText, X, Sparkles, ShieldCheck, AlertTriangle,
  ArrowRight, Loader2, PhoneCall, Undo2, CheckCircle2,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import RisultatoRegola from '../components/sindacato/RisultatoRegola';

import {
  getCcnlDisponibili, estraiCedolino, caricaCedolino,
  richiediContattoOperatore, revocaContattoOperatore,
} from '../services/sindacatoApi';

const SETTORI = [
  { value: 'logistica', label: 'Logistica' },
  { value: 'trasporto', label: 'Trasporto' },
  { value: 'corrieri', label: 'Corrieri / rider' },
  { value: 'commercio', label: 'Commercio' },
  { value: 'metalmeccanica', label: 'Metalmeccanica' },
  { value: 'chimico', label: 'Chimico / farmaceutico' },
  { value: 'altro', label: 'Altro' },
];

const TIPI_CONTRATTO = [
  { value: 'dipendente', label: 'Lavoratore dipendente' },
  { value: 'co.co.co', label: 'Collaborazione (co.co.co)' },
  { value: 'p.iva', label: 'Partita IVA' },
  { value: 'altro', label: 'Altro' },
];

const CONSENSO_TRATTAMENTO_TESTO =
  "Autorizzo il trattamento dei dati contenuti nel cedolino che ho caricato, esclusivamente " +
  "per l'esecuzione del controllo gratuito che ho richiesto. Il cedolino e i dati da esso " +
  "estratti saranno cancellati entro un breve periodo dall'analisi e non saranno condivisi con " +
  "terzi, salvo il caso in cui scelga separatamente di essere ricontattato da un consulente.";

const CONSENSO_CONTATTO_TESTO =
  "Acconsento a essere ricontattato/a da un consulente per un approfondimento sul " +
  "controllo effettuato. Il fatto di aver richiesto questo contatto viene trattato con la stessa " +
  "cautela riservata alle categorie particolari di dati (art. 9 GDPR), poiché può rivelare un mio " +
  "interesse verso la tutela dei miei diritti sul lavoro. È trattato solo in base a questo " +
  "consenso esplicito, revocabile in ogni momento con cancellazione dei miei dati di contatto.";

const CEDOLINO_INIZIALE = {
  lordo: '', netto: '', ccnl_dichiarato: '', tfr_accantonato_busta: '',
  ferie_maturate: '', ferie_godute: '', ferie_residue: '',
  permessi_maturati: '', permessi_goduti: '', permessi_residui: '',
  straordinario_ore: '', straordinario_importo: '',
  giorni_malattia: '', importo_malattia_busta: '',
  giorni_infortunio: '', importo_infortunio_busta: '',
  giorni_congedo: '', importo_congedo_busta: '',
};

function numeroOUndefined(v) {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = parseFloat(v);
  return Number.isNaN(n) ? undefined : n;
}


export default function ControlloSindacalePage() {
  const [step, setStep] = useState('benvenuto');
  const [ccnlList, setCcnlList] = useState([]);
  const fileInputRef = useRef(null);

  const [settoreDichiarato, setSettoreDichiarato] = useState('');
  const [tipoContratto, setTipoContratto] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [estraendo, setEstraendo] = useState(false);
  const [cedolino, setCedolino] = useState(CEDOLINO_INIZIALE);
  const [consensoTrattamento, setConsensoTrattamento] = useState(false);
  const [analizzando, setAnalizzando] = useState(false);

  const [risultatoAnalisi, setRisultatoAnalisi] = useState(null);
  const [cedolinoId, setCedolinoId] = useState(null);

  const [consensoContatto, setConsensoContatto] = useState(false);
  const [nome, setNome] = useState('');
  const [recapito, setRecapito] = useState('');
  const [inviandoContatto, setInviandoContatto] = useState(false);

  useEffect(() => {
    getCcnlDisponibili().then(setCcnlList).catch(() => setCcnlList([]));
  }, []);

  const aggiornaCedolino = (campo, valore) => setCedolino((prev) => ({ ...prev, [campo]: valore }));

  const handleFile = (file) => {
    if (!file) return;
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleEstrai = async () => {
    if (!selectedFile) return;
    setEstraendo(true);
    try {
      const estratto = await estraiCedolino(selectedFile);
      // Nota privacy: qui non copiamo mai employee_name / fiscal_code /
      // company_name / raw_text nello stato del form: solo le cifre.
      setCedolino((prev) => ({
        ...prev,
        lordo: estratto.gross_salary ?? prev.lordo,
        netto: estratto.net_salary ?? prev.netto,
        tfr_accantonato_busta: estratto.tfr_accrued ?? prev.tfr_accantonato_busta,
        straordinario_ore: estratto.overtime_hours ?? prev.straordinario_ore,
        straordinario_importo: estratto.overtime_amount ?? prev.straordinario_importo,
      }));
      toast.success('Dati estratti dal cedolino. Verifica e completa i campi mancanti.');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Estrazione automatica non riuscita: compila i campi manualmente.");
    } finally {
      setEstraendo(false);
    }
  };

  const puoInviareStep1 =
    settoreDichiarato && tipoContratto && cedolino.lordo && cedolino.netto && consensoTrattamento;

  const handleSubmitCedolino = async () => {
    setAnalizzando(true);
    try {
      const payload = {
        questionario: { settore_dichiarato: settoreDichiarato, tipo_contratto: tipoContratto },
        cedolino: {
          settore_dichiarato: settoreDichiarato,
          tipo_contratto: tipoContratto,
          ccnl_dichiarato: cedolino.ccnl_dichiarato || undefined,
          lordo: numeroOUndefined(cedolino.lordo),
          netto: numeroOUndefined(cedolino.netto),
          tfr_accantonato_busta: numeroOUndefined(cedolino.tfr_accantonato_busta),
          ferie_maturate: numeroOUndefined(cedolino.ferie_maturate),
          ferie_godute: numeroOUndefined(cedolino.ferie_godute),
          ferie_residue: numeroOUndefined(cedolino.ferie_residue),
          permessi_maturati: numeroOUndefined(cedolino.permessi_maturati),
          permessi_goduti: numeroOUndefined(cedolino.permessi_goduti),
          permessi_residui: numeroOUndefined(cedolino.permessi_residui),
          straordinario_ore: numeroOUndefined(cedolino.straordinario_ore),
          straordinario_importo: numeroOUndefined(cedolino.straordinario_importo),
          giorni_malattia: numeroOUndefined(cedolino.giorni_malattia),
          importo_malattia_busta: numeroOUndefined(cedolino.importo_malattia_busta),
          giorni_infortunio: numeroOUndefined(cedolino.giorni_infortunio),
          importo_infortunio_busta: numeroOUndefined(cedolino.importo_infortunio_busta),
          giorni_congedo: numeroOUndefined(cedolino.giorni_congedo),
          importo_congedo_busta: numeroOUndefined(cedolino.importo_congedo_busta),
        },
        consensoAccettato: consensoTrattamento,
      };
      const risposta = await caricaCedolino(payload);
      setRisultatoAnalisi(risposta);
      setCedolinoId(risposta.cedolino_id);
      setStep('risultato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il controllo del cedolino.');
    } finally {
      setAnalizzando(false);
    }
  };

  const puoInviareContatto = consensoContatto && nome.trim() && recapito.trim();

  const handleRichiediContatto = async () => {
    setInviandoContatto(true);
    try {
      await richiediContattoOperatore(cedolinoId, { nome, recapito });
      setStep('confermato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la richiesta di contatto.');
    } finally {
      setInviandoContatto(false);
    }
  };

  const handleRevoca = async () => {
    try {
      await revocaContattoOperatore(cedolinoId);
      setStep('revocato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la revoca.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-forest rounded-sm flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-bold text-lg tracking-tight text-forest">BustaControllo</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="max-w-2xl mx-auto space-y-6" data-testid="controllo-sindacale-page">

          {step === 'benvenuto' && (
            <Card className="swiss-card text-center">
              <CardHeader>
                <CardTitle className="text-2xl">Controllo gratuito della busta paga</CardTitle>
                <CardDescription className="text-base mt-2">
                  Carica il tuo cedolino: un controllo generico e gratuito ti segnala eventuali elementi
                  da verificare. Se emerge qualcosa, potrai scegliere se essere ricontattato da un
                  consulente per un approfondimento — nessun obbligo, nessun costo.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="swiss-btn-primary h-12 px-8" onClick={() => setStep('upload')}>
                  Inizia il controllo <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}

          {step === 'upload' && (
            <>
              <Card className="swiss-card">
                <CardHeader>
                  <CardTitle className="text-lg">La tua situazione</CardTitle>
                  <CardDescription>Poche informazioni per orientare il controllo</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">Settore</Label>
                      <Select value={settoreDichiarato} onValueChange={setSettoreDichiarato}>
                        <SelectTrigger className="swiss-input" data-testid="select-settore">
                          <SelectValue placeholder="Seleziona il settore" />
                        </SelectTrigger>
                        <SelectContent>
                          {SETTORI.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Tipo di contratto</Label>
                      <Select value={tipoContratto} onValueChange={setTipoContratto}>
                        <SelectTrigger className="swiss-input" data-testid="select-tipo-contratto">
                          <SelectValue placeholder="Seleziona il tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPI_CONTRATTO.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`swiss-card border-dashed border-2 ${dragActive ? 'border-forest bg-mint/40' : 'border-forest/20 bg-mint/20'}`}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-forest" /> Carica il cedolino
                  </CardTitle>
                  <CardDescription>Trascina qui il PDF o la foto, oppure selezionalo dal dispositivo</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,image/*"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="file-input"
                  />
                  {selectedFile ? (
                    <div className="flex items-center gap-3 p-4 bg-white border border-forest/20 rounded-sm">
                      <FileText className="w-8 h-8 text-forest flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{selectedFile.name}</p>
                        <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => setSelectedFile(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-8 border-2 border-dashed border-slate-300 rounded-sm hover:border-forest/50 hover:bg-mint/50 transition-colors text-center"
                      data-testid="upload-dropzone"
                    >
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="font-medium text-sm">Trascina qui il file oppure clicca per selezionarlo</p>
                    </button>
                  )}
                  {selectedFile && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={estraendo}
                      onClick={handleEstrai}
                      data-testid="extract-btn"
                    >
                      {estraendo ? (
                        <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Estrazione in corso...</span>
                      ) : (
                        <>Prova a compilare automaticamente i campi</>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className="swiss-card">
                <CardHeader>
                  <CardTitle className="text-lg">Verifica i dati</CardTitle>
                  <CardDescription>
                    Compila almeno lordo e netto. Gli altri campi sono facoltativi: se non li conosci
                    lasciali vuoti, quella voce risulterà "non verificabile" invece di generare un
                    controllo impreciso.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase">Lordo mensile *</Label>
                      <Input type="number" step="0.01" className="swiss-input font-mono" data-testid="input-lordo"
                        value={cedolino.lordo} onChange={(e) => aggiornaCedolino('lordo', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase">Netto in busta *</Label>
                      <Input type="number" step="0.01" className="swiss-input font-mono" data-testid="input-netto"
                        value={cedolino.netto} onChange={(e) => aggiornaCedolino('netto', e.target.value)} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="label-uppercase">CCNL indicato in busta (se lo conosci)</Label>
                    <Select value={cedolino.ccnl_dichiarato} onValueChange={(v) => aggiornaCedolino('ccnl_dichiarato', v)}>
                      <SelectTrigger className="swiss-input" data-testid="select-ccnl">
                        <SelectValue placeholder="Non specificato" />
                      </SelectTrigger>
                      <SelectContent>
                        {ccnlList.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />
                  <p className="text-sm font-medium text-muted-foreground">TFR e straordinari (facoltativo)</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">TFR accantonato</Label>
                      <Input type="number" step="0.01" className="swiss-input font-mono"
                        value={cedolino.tfr_accantonato_busta} onChange={(e) => aggiornaCedolino('tfr_accantonato_busta', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Ore straordinario</Label>
                      <Input type="number" step="0.5" className="swiss-input font-mono"
                        value={cedolino.straordinario_ore} onChange={(e) => aggiornaCedolino('straordinario_ore', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Importo straordinario</Label>
                      <Input type="number" step="0.01" className="swiss-input font-mono"
                        value={cedolino.straordinario_importo} onChange={(e) => aggiornaCedolino('straordinario_importo', e.target.value)} />
                    </div>
                  </div>

                  <Separator />
                  <p className="text-sm font-medium text-muted-foreground">Ferie (facoltativo, in giorni)</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Maturate</Label>
                      <Input type="number" step="0.5" className="swiss-input font-mono"
                        value={cedolino.ferie_maturate} onChange={(e) => aggiornaCedolino('ferie_maturate', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Godute</Label>
                      <Input type="number" step="0.5" className="swiss-input font-mono"
                        value={cedolino.ferie_godute} onChange={(e) => aggiornaCedolino('ferie_godute', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Residue</Label>
                      <Input type="number" step="0.5" className="swiss-input font-mono"
                        value={cedolino.ferie_residue} onChange={(e) => aggiornaCedolino('ferie_residue', e.target.value)} />
                    </div>
                  </div>

                  <p className="text-sm font-medium text-muted-foreground">Permessi (facoltativo, in ore)</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Maturati</Label>
                      <Input type="number" step="0.5" className="swiss-input font-mono"
                        value={cedolino.permessi_maturati} onChange={(e) => aggiornaCedolino('permessi_maturati', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Goduti</Label>
                      <Input type="number" step="0.5" className="swiss-input font-mono"
                        value={cedolino.permessi_goduti} onChange={(e) => aggiornaCedolino('permessi_goduti', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Residui</Label>
                      <Input type="number" step="0.5" className="swiss-input font-mono"
                        value={cedolino.permessi_residui} onChange={(e) => aggiornaCedolino('permessi_residui', e.target.value)} />
                    </div>
                  </div>

                  <Separator />
                  <p className="text-sm font-medium text-muted-foreground">
                    Malattia, infortunio, congedi/maternità (facoltativo)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Giorni malattia</Label>
                      <Input type="number" step="1" className="swiss-input font-mono"
                        value={cedolino.giorni_malattia} onChange={(e) => aggiornaCedolino('giorni_malattia', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Importo malattia in busta</Label>
                      <Input type="number" step="0.01" className="swiss-input font-mono"
                        value={cedolino.importo_malattia_busta} onChange={(e) => aggiornaCedolino('importo_malattia_busta', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Giorni infortunio</Label>
                      <Input type="number" step="1" className="swiss-input font-mono"
                        value={cedolino.giorni_infortunio} onChange={(e) => aggiornaCedolino('giorni_infortunio', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Importo infortunio in busta</Label>
                      <Input type="number" step="0.01" className="swiss-input font-mono"
                        value={cedolino.importo_infortunio_busta} onChange={(e) => aggiornaCedolino('importo_infortunio_busta', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Giorni congedo/maternità</Label>
                      <Input type="number" step="1" className="swiss-input font-mono"
                        value={cedolino.giorni_congedo} onChange={(e) => aggiornaCedolino('giorni_congedo', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-uppercase text-xs">Importo congedo in busta</Label>
                      <Input type="number" step="0.01" className="swiss-input font-mono"
                        value={cedolino.importo_congedo_busta} onChange={(e) => aggiornaCedolino('importo_congedo_busta', e.target.value)} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="swiss-card border-forest/20">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consenso-trattamento"
                      checked={consensoTrattamento}
                      onCheckedChange={setConsensoTrattamento}
                      data-testid="checkbox-consenso-trattamento"
                    />
                    <Label htmlFor="consenso-trattamento" className="text-sm leading-relaxed font-normal cursor-pointer">
                      {CONSENSO_TRATTAMENTO_TESTO}
                    </Label>
                  </div>
                </CardContent>
              </Card>

              <Button
                className="swiss-btn-primary w-full h-12"
                disabled={!puoInviareStep1 || analizzando}
                onClick={handleSubmitCedolino}
                data-testid="submit-cedolino-btn"
              >
                {analizzando ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analisi in corso...</span>
                ) : (
                  <>Avvia il controllo gratuito</>
                )}
              </Button>
            </>
          )}

          {step === 'risultato' && risultatoAnalisi && (
            <div className="space-y-4">
              <Card className="swiss-card text-center">
                <CardHeader>
                  {risultatoAnalisi.esito_complessivo === 'OK' ? (
                    <>
                      <ShieldCheck className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                      <CardTitle className="text-xl">Nessun elemento da segnalare</CardTitle>
                      <CardDescription>
                        Il controllo generico non ha rilevato elementi da approfondire. Questo non
                        sostituisce una verifica puntuale del tuo CCNL: se in futuro vuoi comunque
                        essere ricontattato da un operatore, puoi ricaricare il cedolino in un secondo momento.
                      </CardDescription>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-12 h-12 text-amber-600 mx-auto mb-2" />
                      <CardTitle className="text-xl">Alcuni elementi da verificare</CardTitle>
                      <CardDescription>
                        Il controllo generico ha individuato alcuni elementi che meritano un
                        approfondimento. Non si tratta di una diagnosi definitiva: solo un consulente,
                        con il tuo CCNL specifico, può dirti se c'è davvero qualcosa da
                        correggere.
                      </CardDescription>
                    </>
                  )}
                </CardHeader>
              </Card>

              <div className="space-y-3">
                {risultatoAnalisi.risultati.map((r, i) => <RisultatoRegola key={i} risultato={r} />)}
              </div>

              {risultatoAnalisi.richiede_step_contatto ? (
                <Card className="swiss-card border-forest/30 bg-mint/30">
                  <CardContent className="p-5 text-center space-y-3">
                    <p className="font-medium">Vuoi essere ricontattato da un consulente?</p>
                    <p className="text-sm text-muted-foreground">
                      Un consulente rivedrà il tuo caso con il CCNL specifico e ti richiamerà per un
                      approfondimento gratuito. Nessun obbligo.
                    </p>
                    <Button className="swiss-btn-primary" onClick={() => setStep('contatto')} data-testid="vuoi-contatto-btn">
                      <PhoneCall className="w-4 h-4 mr-2" /> Sì, voglio essere ricontattato/a
                    </Button>
                    <Button variant="ghost" className="text-muted-foreground" onClick={() => setStep('benvenuto')}>
                      No, grazie
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="text-center">
                  <Button variant="outline" onClick={() => setStep('benvenuto')}>Torna alla home</Button>
                </div>
              )}
            </div>
          )}

          {step === 'contatto' && (
            <Card className="swiss-card">
              <CardHeader>
                <CardTitle className="text-lg">I tuoi dati di contatto</CardTitle>
                <CardDescription>Servono solo per essere ricontattato/a da un consulente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-uppercase">Nome e cognome</Label>
                  <Input className="swiss-input" value={nome} onChange={(e) => setNome(e.target.value)} data-testid="input-nome" />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Telefono o email</Label>
                  <Input className="swiss-input" value={recapito} onChange={(e) => setRecapito(e.target.value)} data-testid="input-recapito" />
                </div>

                <Card className="border-forest/20 bg-mint/20">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consenso-contatto"
                        checked={consensoContatto}
                        onCheckedChange={setConsensoContatto}
                        data-testid="checkbox-consenso-contatto"
                      />
                      <Label htmlFor="consenso-contatto" className="text-sm leading-relaxed font-normal cursor-pointer">
                        {CONSENSO_CONTATTO_TESTO}
                      </Label>
                    </div>
                  </CardContent>
                </Card>

                <Button
                  className="swiss-btn-primary w-full h-12"
                  disabled={!puoInviareContatto || inviandoContatto}
                  onClick={handleRichiediContatto}
                  data-testid="invia-contatto-btn"
                >
                  {inviandoContatto ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Invio...</span>
                  ) : (
                    <>Invia la richiesta</>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {step === 'confermato' && (
            <Card className="swiss-card text-center">
              <CardHeader>
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                <CardTitle className="text-xl">Richiesta inviata</CardTitle>
                <CardDescription>
                  Un consulente ti ricontatterà a breve. Puoi revocare il consenso e far
                  cancellare i tuoi dati di contatto in qualsiasi momento.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" onClick={handleRevoca} data-testid="revoca-btn">
                  <Undo2 className="w-4 h-4 mr-2" /> Revoca il consenso al contatto
                </Button>
              </CardContent>
            </Card>
          )}

          {step === 'revocato' && (
            <Card className="swiss-card text-center">
              <CardHeader>
                <Undo2 className="w-12 h-12 text-slate-500 mx-auto mb-2" />
                <CardTitle className="text-xl">Consenso revocato</CardTitle>
                <CardDescription>
                  I tuoi dati di contatto sono stati cancellati. Nessun operatore ti contatterà.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" onClick={() => setStep('benvenuto')}>Torna alla home</Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
